import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from './Modal';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  details?: Record<string, any> | string | null;
  warningMessage?: string | null;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'danger' | 'secondary' | 'accent' | 'warning' | 'info';
  isLoading?: boolean;
  /** Use when dialog must appear above other overlays (e.g. above a presets panel at z-[100]) */
  zIndex?: number;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  details = null,
  warningMessage = null,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'danger',
  isLoading = false,
  zIndex,
}) => {
  const { colors } = useTheme();
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} zIndex={zIndex}>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          {/* Main confirmation message */}
          <p className={`text-sm ${colors.textSecondary}`}>
            {message}
          </p>

          {/* Optional details section */}
          {details && (
            <div className={`${colors.surfaceHover} p-3 rounded border ${colors.border}`}>
              <div className={`text-xs ${colors.textSecondary}`}>
                {typeof details === 'object' ? (
                  Object.entries(details).map(([key, value]) => (
                    <div key={key}>
                      <strong>{key}:</strong> {String(value)}
                    </div>
                  ))
                ) : (
                  <div>{details}</div>
                )}
              </div>
            </div>
          )}

          {/* Optional warning message */}
          {warningMessage && (
            <p className={cn('text-sm', colors.error)}>
              {warningMessage}
            </p>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            variant={confirmVariant}
            disabled={isLoading}
            loading={isLoading}
          >
            {confirmText}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmationDialog;

